/* Innate FAQ controls (2026-07-25)
 *
 * Adds a sticky search + category bar to the FAQ page, above the existing
 * stacked `.innate-faq` sections (36 questions across 6 categories).
 *
 * Why JS rather than a new section: the control bar has to sit above all six
 * sections, and adding a section means editing templates/page.faq.json, which
 * does not sync through this theme's GitHub integration. Injecting keeps the
 * question content exactly where it is.
 *
 * Progressive enhancement: with JS off the page still works as six stacked
 * accordions. Filtering only hides rows visually — every question stays in the
 * DOM, so the FAQPage structured data and crawlable text are unaffected.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.innate-faq';
  var ALL_LABEL = 'All';

  function text(el) {
    return (el.textContent || '').toLowerCase();
  }

  function build(sections) {
    var bar = document.createElement('div');
    bar.className = 'innate-faq-controls';

    /* Inner wrapper so the coloured band can run full-bleed while its contents
     * line up with the FAQ sections' own container. Without this the band is
     * narrower than the section below it and reads as a floating box. */
    var inner = document.createElement('div');
    inner.className = 'innate-faq-controls__inner';
    bar.appendChild(inner);

    var total = 0;
    sections.forEach(function (s) {
      total += s.rows.length;
    });

    // --- Search field -----------------------------------------------------
    var field = document.createElement('div');
    field.className = 'innate-faq-controls__field';

    var input = document.createElement('input');
    input.type = 'search';
    input.className = 'innate-faq-controls__input';
    input.placeholder = 'Search questions — e.g. "lead time"';
    input.setAttribute('aria-label', 'Search frequently asked questions');
    field.appendChild(input);
    inner.appendChild(field);

    // --- Category chips ---------------------------------------------------
    var chips = document.createElement('div');
    chips.className = 'innate-faq-controls__chips';

    var buttons = [];
    function makeChip(label, value, count) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'innate-faq-controls__chip';
      b.setAttribute('data-category', value);
      b.setAttribute('aria-pressed', value === '' ? 'true' : 'false');
      b.textContent = count === null ? label : label + ' (' + count + ')';
      if (value === '') b.classList.add('is-active');
      chips.appendChild(b);
      buttons.push(b);
      return b;
    }

    makeChip(ALL_LABEL, '', total);
    sections.forEach(function (s, i) {
      makeChip(s.title, String(i), s.rows.length);
    });
    inner.appendChild(chips);

    // --- Result count (announced to screen readers) -----------------------
    var status = document.createElement('p');
    status.className = 'innate-faq-controls__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    inner.appendChild(status);

    return { bar: bar, input: input, buttons: buttons, status: status, total: total };
  }

  function init() {
    var nodes = document.querySelectorAll(ROOT_SELECTOR);
    if (nodes.length < 2) return; // only worth it on the multi-category page

    var sections = [];
    Array.prototype.forEach.call(nodes, function (node) {
      var titleEl = node.querySelector('.innate-faq__title');
      var rows = Array.prototype.slice.call(node.querySelectorAll('details'));
      if (!rows.length) return;
      sections.push({
        node: node,
        title: titleEl ? titleEl.textContent.trim() : 'Questions',
        rows: rows.map(function (row) {
          return { el: row, haystack: text(row) };
        })
      });
    });
    if (sections.length < 2) return;

    var ui = build(sections);

    /* Insert the bar OUTSIDE the first section's `.shopify-section` wrapper.
     * position:sticky is bounded by its containing block, so leaving the bar
     * inside that wrapper made it stop following once the first category
     * scrolled past. Sitting alongside the section wrappers lets it stick for
     * the whole list. */
    var firstWrapper = sections[0].node.closest('.shopify-section') || sections[0].node;
    firstWrapper.parentNode.insertBefore(ui.bar, firstWrapper);

    /* The theme pins a compact header (`.bar.fixed`, ~108px, z-index 90) once
     * you scroll, so the controls have to sit below it rather than at top:0.
     * Measured at runtime because the height differs between breakpoints. */
    function headerHeight() {
      var header =
        document.querySelector('.section-header .bar.fixed') ||
        document.querySelector('.header .fixed') ||
        document.querySelector('.ih-mhead');
      if (!header) return 0;
      var pos = window.getComputedStyle(header).position;
      if (pos !== 'fixed' && pos !== 'sticky') return 0;
      return Math.round(header.getBoundingClientRect().height);
    }

    function syncHeaderOffset() {
      /* Always publish the measured value, including 0. On mobile the theme's
       * header is not pinned, so there is nothing to clear — previously the
       * `height > 0` guard left the CSS fallback in place and the bar floated
       * below an empty gap. Desktop is unaffected: the header only reports a
       * height once it becomes fixed, which is exactly when the offset is
       * needed, and this handler re-runs on scroll. */
      ui.bar.style.setProperty('--innate-faq-controls-top', headerHeight() + 'px');
    }

    /* Scroll so the first matching category starts just under the header and
     * the sticky bar.
     *
     * This deliberately computes an absolute document position rather than
     * calling scrollIntoView() on the bar: the bar is sticky, so its rect is
     * already pinned near the top and each relative scroll nudged the page a
     * little further down on every click. An absolute target lands in the same
     * place no matter how many times it runs. */
    /* The bar's resolved sticky offset (set from the header height). Used
     * instead of measuring the header live, because the header un-fixes when
     * the page shortens and its height would be read mid-transition. */
    function stickyTop() {
      var value = parseFloat(window.getComputedStyle(ui.bar).top);
      return isNaN(value) ? 108 : Math.round(value);
    }

    function scrollToResults() {
      /* Force a layout flush so the just-hidden categories are already out of
       * the flow. Hiding them shrinks the page, which makes the browser
       * re-clamp the scroll position and can un-fix the header — measuring
       * before that settles produced a target past the end of the page, so the
       * browser clamped to the bottom and the heading ended up off-screen. */
      void document.body.offsetHeight;

      var firstVisible = null;
      for (var i = 0; i < sections.length; i++) {
        if (!sections[i].node.hidden) {
          firstVisible = sections[i].node;
          break;
        }
      }

      /* Aim at the category title rather than the section box: the section has
       * ~86px of internal padding, so aligning its outer edge would park empty
       * space on screen and push the questions down. Targeting the title shows
       * the heading in full and fits as many rows below it as possible. */
      var reference = ui.bar;
      if (firstVisible) {
        reference = firstVisible.querySelector('.innate-faq__title') || firstVisible;
      }

      var offset = stickyTop() + Math.round(ui.bar.getBoundingClientRect().height) + 12;
      var documentPosition = window.scrollY + reference.getBoundingClientRect().top;
      var maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      var target = Math.min(Math.max(0, Math.round(documentPosition - offset)), maxScroll);

      window.scrollTo({ top: target, behavior: 'smooth' });
    }

    syncHeaderOffset();
    window.addEventListener('resize', syncHeaderOffset);
    // The header collapses on first scroll, which changes its height.
    window.addEventListener('scroll', syncHeaderOffset, { passive: true });

    var activeCategory = '';

    function apply() {
      var query = ui.input.value.trim().toLowerCase();
      var shown = 0;

      sections.forEach(function (section, index) {
        var categoryAllowed = activeCategory === '' || activeCategory === String(index);
        var visibleInSection = 0;

        section.rows.forEach(function (row) {
          var matches = categoryAllowed && (query === '' || row.haystack.indexOf(query) !== -1);
          row.el.hidden = !matches;
          if (matches) {
            visibleInSection++;
            shown++;
          } else {
            row.el.open = false;
          }
        });

        // Hide a whole category when nothing in it matches.
        section.node.hidden = visibleInSection === 0;
      });

      if (query === '' && activeCategory === '') {
        ui.status.textContent = ui.total + ' questions';
      } else if (shown === 0) {
        ui.status.textContent = 'No questions match — try a different word.';
      } else {
        ui.status.textContent = 'Showing ' + shown + ' of ' + ui.total + ' questions';
      }
    }

    var debounce;
    ui.input.addEventListener('input', function () {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(apply, 120);
    });

    ui.buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        activeCategory = button.getAttribute('data-category');
        ui.buttons.forEach(function (other) {
          var on = other === button;
          other.classList.toggle('is-active', on);
          other.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        apply();
        /* Synchronously — scrollToResults() flushes layout itself. Deferring to
         * animation frames let the browser's own scroll re-clamp and the
         * header's fixed/unfixed switch run first, which corrupted the target. */
        scrollToResults();
      });
    });

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
